import React from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  ClipboardList,
  Users,
  HeartHandshake,
  Sparkles,
  Info,
  ShieldCheck,
  BookOpen,
  ArrowRight,
  Mail,
  MapPin,
} from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { trackWhatsApp } from "../lib/beacon";

/**
 * /atendimento — Página institucional / educacional em conformidade com as
 * políticas de Landing Page do Google Ads e Meta Ads para o segmento de
 * bem-estar e saúde mental.
 *
 * Regras aplicadas:
 *  1. Foco EDUCACIONAL e HUMANIZADO. Nada de "internação", "cura", números.
 *  2. CTAs neutros: "Fale com um Orientador", "Solicite Informações",
 *     "Saiba Como Ajudar".
 *  3. Sem nomes de substâncias, remédios controlados ou linguagem alarmista.
 *  4. Sem imagens de sofrimento. Ícones e ilustrações neutras.
 *  5. Rodapé com CNPJ, Razão Social, Endereço, Termos e Privacidade + disclaimer.
 *  6. Sem pop-ups, sem cloaking, sem redirecionamento oculto.
 */
const SUPPORT_WA = (process.env.REACT_APP_SUPPORT_WA || "+5511996420785").replace(/[^0-9]/g, "");
const WA_MSG = encodeURIComponent(
  "Olá, gostaria de conversar com um orientador da RAF Direct para receber informações."
);
const WA_URL = `https://wa.me/${SUPPORT_WA}?text=${WA_MSG}`;

const FeatureCard = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition">
    <div className="h-10 w-10 rounded-lg bg-[#0B2B62]/10 text-[#0B2B62] flex items-center justify-center">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{children}</p>
  </div>
);

const AudienceCard = ({ to, icon: Icon, title, description, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0B2B62] hover:shadow-md transition"
  >
    <div className="h-11 w-11 rounded-xl bg-[#0B2B62]/10 text-[#0B2B62] flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1">
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-600 mt-0.5">{description}</div>
    </div>
    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#0B2B62] mt-2 shrink-0" />
  </Link>
);

export default function Atendimento() {
  usePageTitle(
    "Atendimento e orientação familiar",
    "Acolhimento, orientação e suporte humano para famílias que buscam apoio em qualidade de vida, saúde mental e bem-estar. Fale com um orientador 24h."
  );
  return (
    <div className="bg-white" data-testid="atendimento-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F5F8FF] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
              <HeartHandshake className="h-3.5 w-3.5 text-[#0B2B62]" />
              Apoio familiar · Orientação humanizada
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B2B62] leading-tight">
              Acolhimento, orientação e suporte para famílias.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              A RAF Direct é uma plataforma de <strong>orientação e informação</strong> que apoia
              famílias na busca por qualidade de vida, bem-estar e equilíbrio emocional. Conectamos
              você a profissionais e unidades credenciadas em um processo humano, transparente e
              respeitoso.
            </p>

            {/* CTAs neutros */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="atendimento-cta-orientador"
                onClick={() => trackWhatsApp("atendimento_hero")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B2B62] px-6 py-4 text-white font-semibold shadow-lg hover:bg-[#0a2454] transition"
              >
                <MessageCircle className="h-5 w-5" />
                Fale com um Orientador
              </a>
              <Link
                to="/cadastro"
                data-testid="atendimento-cta-informacoes"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border-2 border-[#0B2B62] px-6 py-4 text-[#0B2B62] font-semibold hover:bg-[#0B2B62] hover:text-white transition"
              >
                <Info className="h-5 w-5" />
                Solicite Informações
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Conversa confidencial · Atendimento humano · Sem custo para a família
            </p>
          </div>

          {/* Cards de público */}
          <div className="grid gap-4">
            <AudienceCard
              to="/cadastro"
              icon={HeartHandshake}
              title="Famílias e responsáveis"
              description="Receba orientação inicial e conheça caminhos possíveis para apoiar quem você ama."
              testid="atendimento-card-familia"
            />
            <AudienceCard
              to="/seja-parceiro"
              icon={Users}
              title="Unidades e instituições"
              description="Faça parte da nossa rede credenciada e receba encaminhamentos qualificados."
              testid="atendimento-card-clinica"
            />
            <AudienceCard
              to="/seja-profissional"
              icon={ClipboardList}
              title="Profissionais de saúde e bem-estar"
              description="Ofereça acompanhamento multiprofissional dentro de um ambiente ético e regulado."
              testid="atendimento-card-profissional"
            />
          </div>
        </div>
      </section>

      {/* COMO PODEMOS AJUDAR */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Como podemos ajudar
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">
              Um caminho de apoio, informação e cuidado
            </h2>
            <p className="mt-3 text-slate-600">
              Nossa proposta é oferecer orientação humanizada e informação de qualidade,
              respeitando o momento de cada família.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard icon={MessageCircle} title="Escuta acolhedora">
              Um espaço seguro e confidencial para conversar sobre o momento que a família está
              vivendo, sem julgamentos.
            </FeatureCard>
            <FeatureCard icon={BookOpen} title="Informação de qualidade">
              Materiais e explicações claras sobre acompanhamento multiprofissional, redes de apoio
              e cuidados em bem-estar emocional.
            </FeatureCard>
            <FeatureCard icon={Users} title="Rede credenciada">
              Encaminhamento para unidades e profissionais parceiros que atuam com ética e dentro dos
              seus respectivos conselhos profissionais.
            </FeatureCard>
            <FeatureCard icon={Sparkles} title="Apoio à família">
              Reforçamos a importância da rede de apoio familiar e da participação de todos no
              processo de cuidado.
            </FeatureCard>
            <FeatureCard icon={ShieldCheck} title="Transparência">
              Explicamos com clareza cada etapa. Nada é feito sem seu conhecimento e concordância.
            </FeatureCard>
            <FeatureCard icon={HeartHandshake} title="Continuidade do cuidado">
              Acompanhamento contínuo por profissionais habilitados (psicólogos, terapeutas e demais
              especialidades pertinentes).
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Como funciona
            </div>
            <h2 className="mt-2 text-3xl font-bold text-[#0B2B62]">Três passos, no seu tempo</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              {
                n: "1",
                t: "Você fala com um orientador",
                d: "Pelo WhatsApp ou por um formulário simples, entendemos com respeito o contexto da família.",
              },
              {
                n: "2",
                t: "Analisamos com cuidado",
                d: "Nossos orientadores conversam com você para identificar caminhos possíveis de apoio.",
              },
              {
                n: "3",
                t: "Encaminhamos com transparência",
                d: "Se fizer sentido, indicamos unidades e profissionais credenciados. A decisão é sempre da família.",
              },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 p-6 bg-white">
                <div className="h-10 w-10 rounded-full bg-[#0B2B62] text-white font-bold flex items-center justify-center">
                  {s.n}
                </div>
                <h3 className="mt-4 font-semibold text-lg text-slate-900">{s.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          {/* Isenção de responsabilidade */}
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 leading-relaxed">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Aviso importante</div>
                <p className="mt-1">
                  A RAF Direct é uma plataforma de <strong>orientação e informação</strong>. Não
                  realizamos diagnóstico, prescrição ou tratamento. Quaisquer cuidados em saúde
                  dependem de avaliação profissional presencial, sob responsabilidade técnica
                  exclusiva de profissionais habilitados. Em situações de urgência, procure serviços
                  públicos de emergência (SAMU 192).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0B2B62]">
            Podemos conversar quando você quiser
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Não é preciso ter todas as respostas para começar. Um orientador está disponível para
            escutar e ajudar você a entender os próximos passos.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="atendimento-cta-orientador-bottom"
              onClick={() => trackWhatsApp("atendimento_bottom")}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B2B62] px-6 py-4 text-white font-semibold shadow-lg hover:bg-[#0a2454] transition"
            >
              <MessageCircle className="h-5 w-5" />
              Fale com um Orientador
            </a>
            <Link
              to="/cadastro"
              data-testid="atendimento-cta-saiba-como-ajudar"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border-2 border-[#0B2B62] px-6 py-4 text-[#0B2B62] font-semibold hover:bg-[#0B2B62] hover:text-white transition"
            >
              <BookOpen className="h-5 w-5" />
              Saiba Como Ajudar
            </Link>
          </div>
        </div>
      </section>

      {/* RODAPÉ DE TRANSPARÊNCIA (obrigatório em landing pages sensíveis) */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Dados institucionais
            </div>
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              <img src="/raf-logo.webp" alt="RAF Direct" className="h-6 w-auto" width="36" height="24" decoding="async" loading="lazy" />
              RAF Direct
            </div>
            <div className="mt-3 space-y-1.5 text-slate-700">
              <div>
                <span className="font-semibold">Razão social: </span>
                <span>RAF - Rede de Apoio aos Familiares LTDA</span>
              </div>
              <div>
                <span className="font-semibold">CNPJ: </span>
                <span>61.281.627/0001-52</span>
              </div>
              <div className="flex items-start gap-1.5">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" aria-hidden="true" />
                <span>
                  Rua Basílio Puntel, 500 · Sala 1 · Jardim Cardoso (Mailasqui)
                  <br />
                  São Roque/SP · CEP 18.143-810
                </span>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Contatos oficiais
            </div>
            <ul className="space-y-1.5 text-slate-700">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-500" />
                <a
                  href="mailto:contato@rafdirect.com.br"
                  className="text-[#0B2B62] hover:underline"
                >
                  contato@rafdirect.com.br
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-slate-500" />
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("atendimento_footer")} className="text-[#0B2B62] hover:underline">
                  +{SUPPORT_WA.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, "$1 $2 $3-$4")}
                </a>
              </li>
              <li className="text-slate-500 text-xs">Atendimento: seg. a dom., 24h</li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Documentos legais
            </div>
            <ul className="space-y-1.5">
              <li>
                <Link to="/politica-de-privacidade" className="text-[#0B2B62] hover:underline">
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link to="/termos-de-uso" className="text-[#0B2B62] hover:underline">
                  Termos de Uso
                </Link>
              </li>
              <li>
                <Link to="/compliance" className="text-[#0B2B62] hover:underline">
                  Compliance · Dados institucionais completos
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 text-[11px] text-slate-500 leading-relaxed">
            <p>
              <strong>Isenção de responsabilidade:</strong> a RAF Direct oferece exclusivamente
              serviços de orientação, informação e encaminhamento. Não somos uma clínica e não
              prestamos serviços médicos, psicológicos, psiquiátricos, de diagnóstico, prescrição ou
              tratamento. Qualquer cuidado em saúde depende de avaliação profissional presencial,
              realizada por profissionais habilitados e regularmente inscritos em seus respectivos
              conselhos de classe (CRM, CRP, COREN, entre outros). O conteúdo desta página tem
              caráter meramente informativo e não substitui a orientação profissional individualizada.
            </p>
            <p className="mt-2">© 2026 RAF Direct · Todos os direitos reservados.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
